import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getProjectCreditsSummary } from '../api';
import { creditsToYuan } from '../utils/currency';

interface CostFloatingPillProps {
  projectId: string;
  // 'floating' (default): fixed pill anchored below the header.
  // 'inline': renders inside normal flow (e.g. embedded in the header nav).
  variant?: 'floating' | 'inline';
}

const CostFloatingPill: React.FC<CostFloatingPillProps> = ({ projectId, variant = 'floating' }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [total, setTotal] = useState<number | null>(null);
  const [hover, setHover] = useState(false);
  const [pulsing, setPulsing] = useState(false);
  const prevTotalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!projectId) return;
    const fetchCredits = () => {
      getProjectCreditsSummary(projectId)
        .then(data => {
          const sum = (data || []).reduce((acc, r) => acc + r.totalCredits, 0);
          setTotal(sum);
        })
        .catch(() => {});
    };
    fetchCredits();
    const interval = setInterval(fetchCredits, 30000);
    return () => clearInterval(interval);
  }, [projectId]);

  useEffect(() => {
    if (total !== null && prevTotalRef.current !== null && total !== prevTotalRef.current) {
      setPulsing(true);
      const timer = setTimeout(() => setPulsing(false), 600);
      return () => clearTimeout(timer);
    }
    prevTotalRef.current = total;
  }, [total]);

  if (total === null || total === 0) return null;

  const floatingStyle: React.CSSProperties = {
    position: 'fixed',
    top: 'calc(var(--header-height) + 8px)',
    right: '24px',
    zIndex: 50,
    boxShadow: hover ? 'var(--shadow)' : 'var(--shadow-sm)',
    transform: hover ? 'scale(1.03)' : 'scale(1)',
    animation: pulsing ? 'costPulse 0.6s ease-out, costPillSlideUp 0.3s ease-out' : 'costPillSlideUp 0.3s ease-out',
  };

  return (
    <button
      onClick={() => navigate(`/projects/${projectId}/credits`)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={pulsing ? 'cost-pill-pulse' : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '3px 10px',
        borderRadius: '999px',
        border: '1px solid transparent',
        background: hover ? '#5558e6' : '#6366f1',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: variant === 'inline' ? '0.8rem' : '0.7rem',
        fontWeight: 600,
        color: '#fff',
        transition: 'all 0.15s ease',
        flexShrink: 0,
        ...(variant === 'floating' ? floatingStyle : {}),
      }}
      title={t('costPill.tooltip')}
    >
      <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>{t('costPill.label')}</span>
      <span style={{ transition: 'all 0.3s ease' }}>¥{creditsToYuan(total)}</span>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
        <polyline points="9 18 15 12 9 6"/>
      </svg>
      <style>{`
        @keyframes costPillSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </button>
  );
};

export default CostFloatingPill;
